import React from 'react';
import Navigation from '@/components/Navigation';
import AnimatedBackground from '@/components/AnimatedBackground';
import { motion } from 'framer-motion';
import { 
  Zap, Code2, Cloud, Shield, Database, Wrench
} from 'lucide-react';

const categories = [
  { id: 'cloud', name: 'Cloud & Infrastructure', icon: Cloud },
  { id: 'backend', name: 'Backend & Languages', icon: Code2 },
  { id: 'data', name: 'Data & Storage', icon: Database },
  { id: 'devops', name: 'DevOps & Tools', icon: Wrench },
  { id: 'security', name: 'Security & Compliance', icon: Shield }
];

const skills = {
  cloud: [
    'AWS', 'Microsoft Azure', 'Google Cloud Platform',
    'EC2', 'S3', 'Lambda', 'DynamoDB',
    'CloudFormation', 'CDK', 'IAM', 'Security Groups',
    'CloudWatch', 'CloudTrail', 'Azure Functions', 'Azure Storage', 'Azure Security', 'Azure DevOps',
    'Compute Engine', 'Cloud Storage', 'Cloud Functions', 'Security & IAM'
  ],
  backend: [
    'Python', 'FastAPI', 'Flask', 'Data Processing', 'System Architecture', 'API Development',
    'TypeScript', 'Node.js', 'Express.js', 'Type System', 'System Design',
    'Go', 'Microservices', 'Concurrency', 'System Development', 'API Design',
    'BunJS'
  ],
  data: [
    'Graph Databases', 'Neo4j', 'AWS Neptune', 'Graph Modeling', 'Query Optimization',
    'Relational Databases', 'PostgreSQL', 'MySQL', 'Oracle', 'Performance Tuning',
    'NoSQL', 'DynamoDB', 'DocumentDB', 'Redis', 'Memcached', 'ElastiCache'
  ],
  devops: [
    'Orchestration', 'Netflix Conductor', 'Azure Step Functions', 'Workflow Design', 'Process Automation',
    'CI/CD', 'GitHub Actions', 'Jenkins', 'Pipeline Design', 'Automation',
    'Infrastructure', 'Terraform', 'Docker', 'Kubernetes', 'Cloud Architecture'
  ],
  security: [
    'Cloud Security', 'IAM & Access Control', 'Security Architecture', 'Compliance', 'Threat Detection',
    'Data Security', 'Encryption', 'Data Protection', 'Security Policies', 'Audit & Monitoring',
    'Identity Security', 'NHI Security', 'Identity Management', 'Access Governance', 'Authentication & Authorization',
    'Vulnerability Management', 'Security Scanning', 'Penetration Testing', 'Risk Assessment', 'Security Patching'
  ]
};

const Skills: React.FC = () => {
  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <Navigation />
      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative mb-8"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-xl transform -rotate-1"></div>
            <div className="relative bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-center gap-3 mb-3">
                <motion.div 
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="p-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg"
                >
                  <Zap className="w-5 h-5 text-white" />
                </motion.div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                  Technical Skills
                </h1>
              </div>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-sm text-gray-600 dark:text-gray-300 text-center"
              >
                A quick overview of my core technical skills by domain.
              </motion.p>
            </div>
          </motion.div>

          {/* Horizontal Skill Lists (now wrapped, no scroll) */}
          <div className="space-y-6">
            {categories.map((cat) => (
              <div
                key={cat.id}
                className="relative group bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 hover:border-green-500/50 dark:hover:border-green-500/50 transition-all duration-300 hover:shadow-lg px-4 py-5 flex items-start gap-4"
              >
                <div className="flex-shrink-0 flex flex-col items-center min-w-[120px]">
                  <div className="p-2 mb-1 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg">
                    <cat.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-green-700 dark:text-green-300 text-center">
                    {cat.name}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap gap-2 items-center">
                    {skills[cat.id].map((skill) => (
                      <span
                        key={skill}
                        className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full whitespace-nowrap"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Skills; 